import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom, ProjectSubmissionToken, Role } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Group,
    NumberInput,
    Pagination,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import {
    IconAlertCircle,
    IconCheck,
    IconClipboard,
    IconCopy,
    IconFilePlus,
    IconLink,
    IconTrash
} from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ProjectSubmissions from "../user/ProjectSubmissionsBrowser";
import AdminClassroomCreator from "./ClassroomCreationForm";
import AdminClassroomDeletionForm from "./ClassroomDeletionForm";
import AdminClassroomDuplicator from "./ClassroomDuplicationForm";
import ClassroomProjectsBrowser from "./ClassroomProjectsBrowser";
import AdminClassroomSongAssignmentForm from "./ClassroomSongAssignmentForm";
import ClassroomSubmissionTokensBrowser from "./ClassroomSubmissionTokensBrowser";
import ClassroomUsersBrowser from "./ClassroomUsersBrowser";

interface ClassroomsBrowserProps {
    onClose: () => void;
}

const ClassroomsManager: React.FC<ClassroomsBrowserProps> = ({ onClose }) => {
    // Use 80vh for the container height and 50px for each row (as in the reference)
    const height = 80;
    const rowHeight = 50;

    const [classrooms, setClassrooms] = useState<Classroom[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [classroomsPerPage, setClassroomsPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (!auth) {
        throw new Error("ClassroomsBrowser must be a child of AuthContext.");
    }
    const token = auth.token;
    if (!token) return null;

    // Calculate classrooms per page based on the viewport height
    const calculateClassroomsPerPage = useCallback(() => {
        // Use 80% of the viewport height minus an estimated 100px for headers/margins
        const containerHeight = window.innerHeight * (height / 100) - 100;
        const perPage = Math.floor(containerHeight / rowHeight);
        setClassroomsPerPage(perPage);
        if (classrooms && perPage * (desiredPage - 1) + 1 > classrooms.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, classrooms]);

    useEffect(() => {
        calculateClassroomsPerPage();
        window.addEventListener("resize", calculateClassroomsPerPage);
        return () => window.removeEventListener("resize", calculateClassroomsPerPage);
    }, [calculateClassroomsPerPage]);

    const fetchClassrooms = () => {
        setLoading(true);

        const notificationId = Utils.displayNotification(
            "Loading Classrooms",
            "Fetching all classrooms, please wait...",
            "blue",
            "loading"
        );

        ConductionAPI.getAllClassrooms(token)
            .then((loadedClassroomsInfo: Classroom[] | null) => {
                if (!loadedClassroomsInfo || loadedClassroomsInfo.length === 0) {
                    setClassrooms(null);
                    Utils.updateNotification(
                        notificationId,
                        "No Classrooms Found",
                        "No classrooms are currently available.",
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    setClassrooms(
                        loadedClassroomsInfo.sort(
                            (a, b) =>
                                b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
                        )
                    );
                    Utils.updateNotification(
                        notificationId,
                        "Classrooms Loaded",
                        "All classrooms have been retrieved.",
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Classrooms",
                    "An error occurred while retrieving classrooms.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchClassrooms();
    }, [token]);

    const paginatedClassrooms = useMemo(() => {
        const start = (desiredPage - 1) * classroomsPerPage;
        return classrooms?.slice(start, start + classroomsPerPage) ?? [];
    }, [desiredPage, classrooms, classroomsPerPage]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            Utils.displayNotification(
                "Copied to Clipboard",
                `"${text}" is ready to paste!`,
                "green",
                <IconClipboard size={16} />
            );
        });
    };

    const handleClassroomAdminLimitChange = (classroomId: string, newLimit: number) => {
        if (newLimit < 0) {
            Utils.displayNotification(
                "Invalid Value",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        const notificationId = Utils.displayNotification(
            "Updating classroom admin limit...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.setClassroomAdminLimit(classroomId, newLimit, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "Updated classrooms admin limit",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchClassrooms();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const handleStudentLimitChange = (classroomId: string, newLimit: number) => {
        if (newLimit < 0) {
            Utils.displayNotification(
                "Invalid Value",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        const notificationId = Utils.displayNotification(
            "Updating student limit...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.setStudentLimit(classroomId, newLimit, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "Updated student limit",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchClassrooms();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const handleRename = (classroomId: string, newName: string) => {
        if (newName.trim().length <= 0) {
            Utils.displayNotification(
                "Invalid Name",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        const notificationId = Utils.displayNotification(
            "Renaming classroom...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.renameClassroom(classroomId, newName, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "Renamed classroom",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchClassrooms();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const [showClassroomCreationModal, setShowClassroomCreationModal] = useState<boolean>(false);
    const [showClassroomDeletionModalFor, setShowClassroomDeletionModalFor] = useState<Classroom | null>(null);
    const [showClassroomDuplicationModalFor, setShowClassroomDuplicationModalFor] = useState<Classroom | null>(null);
    const [showClassroomSongAssignmentModalFor, setShowClassroomSongAssignmentModalFor] = useState<Classroom | null>(null);
    const [showClassroomUsersBrowserModalFor, setShowClassroomUsersBrowserModalFor] = useState<{ classroom: Classroom, defaultRoleFilter: Role } | null>(null);
    const [showClassroomProjectsBrowserModalFor, setShowClassroomProjectsBrowserModalFor] = useState<Classroom | null>(null);
    const [showClassroomProjectSubmissionsBrowserModalFor, setShowClassroomProjectSubmissionsBrowserModalFor] = useState<Classroom | null>(null);
    const [showClassroomSubmissionTokensBrowserModalFor, setShowClassroomSubmissionTokensBrowserModalFor] = useState<Classroom | null>(null);
    const renderModals = () => (
        <>
            {showClassroomCreationModal && (
                <AdminClassroomCreator
                    onClose={() => setShowClassroomCreationModal(false)}
                    onCreated={() => fetchClassrooms()}
                />
            )}

            {showClassroomDeletionModalFor && (
                <AdminClassroomDeletionForm
                    classroom={showClassroomDeletionModalFor}
                    onClose={() => setShowClassroomDeletionModalFor(null)}
                    onDeleted={() => fetchClassrooms()}
                />
            )}

            {showClassroomSongAssignmentModalFor && (
                <AdminClassroomSongAssignmentForm
                    classroom={showClassroomSongAssignmentModalFor}
                    onClose={() => setShowClassroomSongAssignmentModalFor(null)}
                    onAssigned={() => fetchClassrooms()}
                />
            )}

            {showClassroomDuplicationModalFor && (
                <AdminClassroomDuplicator
                    classroom={showClassroomDuplicationModalFor}
                    onClose={() => setShowClassroomDuplicationModalFor(null)}
                    onDuplicated={() => fetchClassrooms()}
                />
            )}

            {showClassroomUsersBrowserModalFor && (
                <ClassroomUsersBrowser
                    classroom={showClassroomUsersBrowserModalFor.classroom}
                    defaultRoleFilter={showClassroomUsersBrowserModalFor.defaultRoleFilter}
                    onClose={() => setShowClassroomUsersBrowserModalFor(null)}
                />
            )}

            {showClassroomProjectsBrowserModalFor && (
                <ClassroomProjectsBrowser
                    classroom={showClassroomProjectsBrowserModalFor}
                    onClose={() => setShowClassroomProjectsBrowserModalFor(null)}
                    onProjectSelected={() => onClose()} // Close out the classrooms manager when loading projects
                />
            )}

            {showClassroomProjectSubmissionsBrowserModalFor && (
                <ProjectSubmissions
                    classroom={showClassroomProjectSubmissionsBrowserModalFor}
                    onClose={() => setShowClassroomProjectSubmissionsBrowserModalFor(null)}
                />
            )}

            {showClassroomSubmissionTokensBrowserModalFor && (
                <ClassroomSubmissionTokensBrowser
                    classroom={showClassroomSubmissionTokensBrowserModalFor}
                    onClose={() => setShowClassroomSubmissionTokensBrowserModalFor(null)}
                    onUpdated={() => fetchClassrooms()}
                />
            )}
        </>
    );

    return (
        <ConductionModal
            onClose={onClose}
            size={"100%"}
            title={"Manage Classrooms"}
        >
            {renderModals()}
            <Stack align="center" justify="center">
                <Button
                    onClick={() => setShowClassroomCreationModal(true)}
                >
                    <IconFilePlus size={16} style={{ marginRight: 8 }} />
                    Create New Classroom
                </Button>

                <Flex
                    direction="column"
                    align="center"
                    justify="space-between"
                    style={{ height: `${height}vh`, width: "100%" }}
                >
                    {loading ? (
                        <Skeleton height="100%" radius="md" />
                    ) : classrooms === null ? (
                        <Text>No classrooms found.</Text>
                    ) : (
                        <ScrollArea style={{ width: "100%" }}>
                            <Table striped stickyHeader highlightOnHover withColumnBorders>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Created</Table.Th>
                                        <Table.Th>Classroom Admins</Table.Th>
                                        <Table.Th>Students</Table.Th>
                                        <Table.Th>Projects</Table.Th>
                                        <Table.Th>Submissions / Credits</Table.Th>
                                        <Table.Th>Songs in Library</Table.Th>
                                        <Table.Th>Admin Access Code</Table.Th>
                                        <Table.Th>Student Access Code</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {paginatedClassrooms.map((classroom) => {
                                        const usedActiveCredits = classroom.tokens?.reduce((acc: number, token: ProjectSubmissionToken) => acc + (token.isActive() ? token.submissions.length : 0), 0) ?? 0
                                        const totalActiveCredits = classroom.tokens?.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getActiveCredits(), 0) ?? 0;
                                        return (
                                            <Table.Tr key={classroom.name} style={{ minHeight: rowHeight }}>
                                                <Table.Td>
                                                    <Group>
                                                        <TextInput
                                                            value={classroom.desiredName}
                                                            maxLength={30}
                                                            onChange={(e) => {
                                                                const newClassrooms = classrooms.map((c) => {
                                                                    if (c.id != classroom.id) return c;
                                                                    return classroom.setDesiredName(e.target.value);
                                                                });
                                                                setClassrooms(newClassrooms);
                                                            }}
                                                        />
                                                        <Button
                                                            size={"xs"}
                                                            disabled={classroom.name === classroom.desiredName}
                                                            onClick={() => handleRename(classroom.id, classroom.desiredName)}
                                                        >
                                                            <IconCheck size={16} />
                                                        </Button>
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td><Badge color="blue">{formatDateString(classroom.dateCreated)}</Badge></Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"flex-start"}
                                                        align={"center"}
                                                    >
                                                        <Tooltip label={`View classroom admins from the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="subtle"
                                                                size="xs"
                                                                onClick={() => setShowClassroomUsersBrowserModalFor({ classroom: classroom, defaultRoleFilter: "ClassroomAdmin" })}
                                                            >
                                                                {classroom.getClassroomAdmins()?.length ?? 0}
                                                            </Button>
                                                        </Tooltip>
                                                        <Group>
                                                            /
                                                            <Tooltip label={`Edit classroom admin limit for the ${classroom.name} classroom`} withArrow>
                                                                <NumberInput
                                                                    style={{
                                                                        width: 70
                                                                    }}
                                                                    value={classroom.desiredClassroomAdminLimit}
                                                                    min={0}
                                                                    max={50}
                                                                    onChange={(value) => {
                                                                        if (typeof value === "string") return;
                                                                        const newClassrooms = classrooms.map((c) => {
                                                                            if (c.id != classroom.id) return c;
                                                                            return classroom.setDesiredClassroomAdminLimit(value);
                                                                        });
                                                                        setClassrooms(newClassrooms);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Button
                                                                size={"xs"}
                                                                disabled={classroom.classroomAdminLimit === classroom.desiredClassroomAdminLimit}
                                                                onClick={() => handleClassroomAdminLimitChange(classroom.id, classroom.desiredClassroomAdminLimit)}
                                                            >
                                                                <IconCheck size={16} />
                                                            </Button>
                                                        </Group>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"flex-start"}
                                                        align={"center"}
                                                    >
                                                        <Tooltip label={`View students from the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="subtle"
                                                                size="xs"
                                                                onClick={() => setShowClassroomUsersBrowserModalFor({ classroom: classroom, defaultRoleFilter: "Student" })}
                                                            >
                                                                {classroom.getClassroomStudents()?.length ?? 0}
                                                            </Button>
                                                        </Tooltip>
                                                        <Group>
                                                            /
                                                            <Tooltip label={`Edit student limit for the ${classroom.name} classroom`} withArrow>
                                                                <NumberInput
                                                                    style={{
                                                                        width: 70
                                                                    }}
                                                                    value={classroom.desiredStudentLimit}
                                                                    min={0}
                                                                    max={500}
                                                                    onChange={(value) => {
                                                                        if (typeof value === "string") return;
                                                                        const newClassrooms = classrooms.map((c) => {
                                                                            if (c.id != classroom.id) return c;
                                                                            return classroom.setDesiredStudentLimit(value);
                                                                        });
                                                                        setClassrooms(newClassrooms);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Button
                                                                size={"xs"}
                                                                disabled={classroom.studentLimit === classroom.desiredStudentLimit}
                                                                onClick={() => handleStudentLimitChange(classroom.id, classroom.desiredStudentLimit)}
                                                            >
                                                                <IconCheck size={16} />
                                                            </Button>
                                                        </Group>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Tooltip label={`View projects from the ${classroom.name} classroom`} withArrow>
                                                        <Button
                                                            variant="subtle"
                                                            size="xs"
                                                            onClick={() => setShowClassroomProjectsBrowserModalFor(classroom)}
                                                        >
                                                            {classroom.projectCount}
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Tooltip label={`View submissions from the ${classroom.name} classroom`} withArrow>
                                                        <Button
                                                            variant="subtle"
                                                            size="xs"
                                                            onClick={() => setShowClassroomProjectSubmissionsBrowserModalFor(classroom)}
                                                        >
                                                            {usedActiveCredits}
                                                        </Button>
                                                    </Tooltip>
                                                    /
                                                    <Tooltip label={`Edit submission credit tokens for the ${classroom.name} classroom`} withArrow>
                                                        <Button
                                                            variant="subtle"
                                                            size="xs"
                                                            onClick={() => setShowClassroomSubmissionTokensBrowserModalFor(classroom)}
                                                        >
                                                            {totalActiveCredits}
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Tooltip label={`Edit song library for the ${classroom.name} classroom`} withArrow>
                                                        <Button
                                                            variant="subtle"
                                                            size="xs"
                                                            onClick={() => setShowClassroomSongAssignmentModalFor(classroom)}
                                                        >
                                                            {classroom.songIds.length}
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"space-between"}
                                                    >
                                                        <Tooltip label={`Classroom Admin code for the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="xs"
                                                                leftSection={<IconCopy size={14} />}
                                                                onClick={() =>
                                                                    classroom.classroomAdminAccessCode &&
                                                                    copyToClipboard(classroom.classroomAdminAccessCode)
                                                                }
                                                            >
                                                                {classroom.classroomAdminAccessCode}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip label={`Classroom Admin shortcut for the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-md"
                                                                onClick={() =>
                                                                    classroom.classroomAdminAccessCode &&
                                                                    copyToClipboard(`${window.location.origin}/register/${classroom.classroomAdminAccessCode}`)
                                                                }
                                                            >
                                                                <IconLink size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"space-between"}
                                                    >
                                                        <Tooltip label={`Student code for the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="xs"
                                                                leftSection={<IconCopy size={14} />}
                                                                onClick={() =>
                                                                    classroom.studentAccessCode &&
                                                                    copyToClipboard(classroom.studentAccessCode)
                                                                }
                                                            >
                                                                {classroom.studentAccessCode}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip label={`Student shortcut for the ${classroom.name} classroom`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-md"
                                                                onClick={() =>
                                                                    classroom.classroomAdminAccessCode &&
                                                                    copyToClipboard(`${window.location.origin}/register/${classroom.studentAccessCode}`)
                                                                }
                                                            >
                                                                <IconLink size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group>
                                                        <Tooltip label="Duplicate Classroom" withArrow>
                                                            <Button
                                                                size="xs"
                                                                onClick={() => setShowClassroomDuplicationModalFor(classroom)}
                                                            >
                                                                <IconCopy size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            label={"Delete"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="xs"
                                                                disabled={loading}
                                                                onClick={() => setShowClassroomDeletionModalFor(classroom)}
                                                            >
                                                                <IconTrash size={20} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}

                    {classrooms && classrooms.length > classroomsPerPage && (
                        <Pagination
                            total={Math.ceil(classrooms.length / classroomsPerPage)}
                            value={desiredPage}
                            onChange={setDesiredPage}
                        />
                    )}
                </Flex>
            </Stack>
        </ConductionModal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleDateString();
}

export default ClassroomsManager;
